/**
 * TotalSales
 * --
 */
import { useState, useEffect } from 'react';
import { Content, Title, Row, Col } from 'components';
import { Divider, Button, Table, Radio, Spin, Statistic } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import {
  CartesianGrid,
  Tooltip,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Legend,
  Bar,
} from 'recharts';
import MessageAlert, { strCut, stringToMoneyFormat } from 'utils';
import moment from 'moment';
import dayjs from 'dayjs';
import { StatisticsApi, OrderApi } from 'api';

const MOMENT_FORMAT = 'YYYY-MM-DD(HH:mm:ss)';
const today = moment().format(MOMENT_FORMAT);

const daysArray = [];
for (let i = 0; i < 7; i++) {
  const date = moment().subtract(i, 'days').format('YYYY-MM-DD');
  daysArray.push(date);
}

/**----------------------------------------
 * [Component] 홈 > 매장별 매출 차트
 * ----------------------------------------
 */
const TotalSales = ({ list, mallList, tableList, isMobile, permission }) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  const [loading, setLoading] = useState(false);
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  /**
   *
   * --
   */
  const totalList = list
    .sort((a, b) => {
      const aaa = moment(a.date).format('YYYY-MM-DD');
      const bbb = moment(b.date).format('YYYY-MM-DD');
      return aaa < bbb ? -1 : aaa > bbb ? 1 : 0;
    })
    .reduce((acc, cur) => {
      const d = moment(cur.date).format('YYYY-MM-DD');

      if (!acc || !acc[d]) {
        acc[d] = {
          totalAmount: 0,
          paymentCount: 0,
          netsAmount: 0,
          discountAmount: 0,
        };
      }
      acc[d]['totalAmount'] = acc[d]['totalAmount'] + Number(cur.totalAmount);
      acc[d]['paymentCount'] =
        acc[d]['paymentCount'] + Number(cur.paymentCount ?? 0);

      acc[d]['netsAmount'] = acc[d]['netsAmount'] + Number(cur.netsAmount);
      acc[d]['discountAmount'] =
        acc[d]['discountAmount'] + Number(cur.discountAmount);
      return acc;
    }, {});
  const dateList = mallList
    .sort((a, b) => {
      const aaa = moment(a.date).format('YYYY-MM-DD');
      const bbb = moment(b.date).format('YYYY-MM-DD');
      return aaa < bbb ? -1 : aaa > bbb ? 1 : 0;
    })
    .reduce((acc, cur) => {
      const d = moment(cur.date).format('YYYY-MM-DD');

      if (!acc || !acc[d]) {
        acc[d] = {
          totalAmount: 0,
          paymentCount: 0,
          netsAmount: 0,
          discountAmount: 0,
        };
      }
      acc[d]['totalAmount'] = acc[d]['totalAmount'] + Number(cur.totalAmount);
      acc[d]['paymentCount'] =
        acc[d]['paymentCount'] + Number(cur.paymentCount ?? 0);

      acc[d]['netsAmount'] = acc[d]['netsAmount'] + Number(cur.netsAmount);
      acc[d]['discountAmount'] =
        acc[d]['discountAmount'] + Number(cur.discountAmount);
      return acc;
    }, {});

  /*  */
  const data = dateList
    ? Object.keys(dateList).map((item) => ({
        date: item,
        ...dateList[item],
      }))
    : [];
  /*  */
  const data2 = totalList
    ? Object.keys(totalList).map((item) => ({
        date: item,
        ...totalList[item],
      }))
    : [];

  /* 매장목록 */
  const sl =
    tableList &&
    tableList
      .sort((a, b) => a.shop_information_id - b.shop_information_id)
      .reduce((acc, cur) => {
        if (!acc[cur.shop_information_name]) {
          acc[cur.shop_information_name] = [];
        }
        if (cur.date) {
          acc[cur.shop_information_name] = [
            ...acc[cur.shop_information_name],
            cur,
          ];
        }
        return acc;
      }, {});
  /* 날짜목록 */
  const dl =
    tableList &&
    [...tableList, ...data].reduce((acc, cur) => {
      if (!acc[cur.date]) {
        acc[cur.date] = {};
      }
      return acc;
    }, {});

  /*  */
  const ssl = {
    // ['쇼핑몰']: data ?? [],
    ...sl,
  };
  const shopList = ssl ? Object.keys(ssl).map((item) => item) : [];

  /*  */
  const dayList = dl
    ? Object.keys(dl)
        .filter((item) => item !== null && item !== 'null')
        .map((item) => item)
        .sort()
        .reverse()
    : [];

  // console.log('[ ] shopList: ', shopList);
  // console.log('[ ] ssl: ', ssl);
  // console.log('[ ] dl: ', dl);
  // console.log('[ ] data: ', data);
  // console.log('[ ] dateList: ', dateList);
  // console.log('[ ] dayList: ', dayList);
  // console.log('[ ] dayList: ', dayList);

  // JSX: start
  return (
    <>
      <Spin spinning={loading}>
        <Row gutter={[5, 5]}>
          {/* ===== 차트: start ===== */}
          <Col x={permission === 'ADMIN' ? (isMobile ? 24 : 10) : 24}>
            <Content
              maxWidth={'100%'}
              borderRadius={4}
              padding={isMobile ? 10 : 20}
              height={isMobile ? 325 : 465}
              style={{ overflowY: 'auto' }}
            >
              <p size={5}>{today}기준</p>

              <ResponsiveContainer
                width="100%"
                minWidth={400}
                height={isMobile ? 260 : 390}
                style={{ marginTop: 15 }}
              >
                <BarChart
                  width={500}
                  height={isMobile ? 260 : 390}
                  data={
                    !data2
                      ? []
                      : data2?.map((item) => ({
                          ...item,
                          date: moment(item.date).format('MM/DD'),
                          ['amt']: Number(item?.totalAmount ?? 0),
                          ['총매출']: Number(item?.totalAmount ?? 0),
                          ['순매출']: Number(item?.netsAmount ?? 0),
                          // ['순매출']: Number(item.netsAmount),
                        }))
                  }
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="6 7" />
                  {!isMobile && <YAxis dataKey="총매출" />}
                  <XAxis type="category" dataKey="date" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="총매출" fill="#82ca9d" />
                  <Bar dataKey="순매출" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Content>
          </Col>
          {/* ===== 차트: end ===== */}
          {/* ===== 테이블: start ===== */}
          {permission === 'ADMIN' && (
            <Col x={isMobile ? 24 : 14}>
              <Content maxWidth={'100%'} height={475} borderRadius={4}>
                <Title size={5} style={{ marginBottom: 12 }}>
                  전체 일별 매출
                </Title>
                <Divider style={{ margin: '12px 0' }} />

                <Table
                  bordered
                  size="small"
                  scroll={{ x: 760, y: 290 }}
                  pagination={false}
                  dataSource={shopList ?? []}
                  style={{
                    borderBottom: '1px solid #f1f1f1',
                    lineHeight: '115%',
                  }}
                  columns={[
                    {
                      title: '매장＼날짜',
                      dataIndex: 'date',
                      width: isMobile ? 110 : 120,
                      align: 'center',
                      ...(!isMobile && { fixed: 'left' }),
                      render: (_, v) => (
                        <p
                          style={{
                            lineHeight: '130%',
                            fontSize: isMobile ? '0.92em' : '1em',
                          }}
                        >
                          {v}
                        </p>
                      ),
                    },
                    ...dayList.map((item) => {
                      return {
                        title: (
                          <p
                            style={{
                              lineHeight: '135%',
                              fontSize: isMobile ? '0.85em' : '1em',
                            }}
                          >
                            {item}
                            <br />
                            총매출(순매출)
                          </p>
                        ),
                        dataIndex: `${item}`,
                        width: isMobile ? 120 : 130,
                        align: 'center',
                        // fixed: 'left',
                        render: (_, v) => {
                          const totalAmount =
                            ssl[v].find((i) => i.date === item)?.totalAmount ??
                            0;
                          const netsAmount =
                            ssl[v].find((i) => i.date === item)?.netsAmount ??
                            0;
                          return (
                            <p style={{ lineHeight: '125%' }}>
                              {stringToMoneyFormat(Number(totalAmount ?? 0))}
                              <br />
                              <small>
                                ({stringToMoneyFormat(Number(netsAmount ?? 0))})
                              </small>
                            </p>
                          );
                        },
                      };
                    }),
                  ]}
                  summary={(row, rr) => {
                    const totalAmount = dayList.map((day) =>
                      data2
                        .filter((i) => i.date === day)
                        .reduce((acc, cur) => (acc += cur.totalAmount ?? 0), 0)
                    );
                    const netsAmount = dayList.map((day) =>
                      data2
                        .filter((i) => i.date === day)
                        .reduce((acc, cur) => (acc += cur.netsAmount ?? 0), 0)
                    );

                    return (
                      <Table.Summary fixed>
                        <Table.Summary.Row align="center">
                          <Table.Summary.Cell index={0}>
                            <b>합계({shopList.length ?? 0})</b>
                          </Table.Summary.Cell>

                          {totalAmount.map((item, idx) => (
                            <Table.Summary.Cell
                              index={idx + 1}
                              key={`asdwo${idx}`}
                            >
                              <p style={{ lineHeight: '125%' }}>
                                {stringToMoneyFormat(item)}
                                <br />({stringToMoneyFormat(netsAmount[idx])})
                              </p>
                            </Table.Summary.Cell>
                          ))}
                        </Table.Summary.Row>
                      </Table.Summary>
                    );
                  }}
                />
                {/* <Table
                bordered
                size="small"
                scroll={{ x: 720, y: 290 }}
                pagination={false}
                dataSource={data.reverse() ?? []}
                style={{ borderBottom: '1px solid #f1f1f1' }}
                columns={[
                  {
                    title: '날짜',
                    dataIndex: 'date',
                    width: 100,
                    align: 'center',
                    fixed: 'left',
                    render: (v) => moment(v).format('YYYY-MM-DD(ddd)'),
                  },
                  {
                    title: '총매출',
                    dataIndex: 'totalAmount',
                    width: 100,
                    align: 'center',
                    render: (value) =>
                      `${stringToMoneyFormat(Number(value))}원`,
                  },
                  {
                    title: '순매출',
                    dataIndex: 'netsAmount',
                    width: 100,
                    align: 'center',
                    render: (value) =>
                      `${stringToMoneyFormat(Number(value))}원`,
                  },
                  {
                    title: '주문건수',
                    dataIndex: 'paymentCount',
                    width: 100,
                    align: 'center',
                    render: (value) =>
                      `${stringToMoneyFormat(Number(value))}건`,
                  },
                  {
                    title: '할인금액',
                    dataIndex: 'discountAmount',
                    width: 100,
                    align: 'center',
                    render: (value) =>
                      `${stringToMoneyFormat(Number(value))}원`,
                  },
                ]}
                summary={(row) => {
                  const totalAmount = row.reduce(
                    (acc, cur) => acc + Number(cur.totalAmount),
                    0
                  );
                  const netsAmount = row.reduce(
                    (acc, cur) => acc + Number(cur.netsAmount),
                    0
                  );
                  const paymentCount = row.reduce(
                    (acc, cur) => acc + Number(cur.paymentCount),
                    0
                  );
                  const discountAmount = row.reduce(
                    (acc, cur) => acc + Number(cur.discountAmount),
                    0
                  );

                  return (
                    <Table.Summary fixed>
                      <Table.Summary.Row align="center">
                        <Table.Summary.Cell index={0}>
                          <b>합계</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <b>{stringToMoneyFormat(totalAmount)}</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>
                          <b>{stringToMoneyFormat(netsAmount)}</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                          <b>{stringToMoneyFormat(paymentCount)}</b>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                          <b>{stringToMoneyFormat(discountAmount)}</b>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              /> */}
              </Content>
            </Col>
          )}
          {/* ===== 테이블: end ===== */}
        </Row>
      </Spin>
    </>
  ); // JSX: end
};

TotalSales.defaultProps = {
  storeSales: [],
  orderList: [],
  list: [],
  // dateList: [],
  // dateList: [],
};
export default TotalSales;
